import { Grid } from "@mui/material";
import { useCallback, useContext, useEffect, useRef, useState } from "react";
import Button from "../../../framework/Button";
import Checkbox from "../../../framework/Checkbox";
import GlobalContext from "../../../framework/GlobalContext";
import NumberInput from "../../../framework/NumberInput";
import SelectInput from "../../../framework/SelectInput";
import Shortcut from "../../../framework/Shortcut";
import TextInput from "../../../framework/TextInput";
import { displayToast } from "../../../framework/toast/toastUtils";
import { getCustomerCategories } from "../../../services/customer-category-services";
import { saveEntity } from "../../../services/entity-services";
import { stateCodes } from "../../../utils/constant";
import { getBankList } from "../../../services/bank-services";
import { formStateHandler, isValidPhoneNumber } from "../../../utils/utils";
import {
  customerTypeOptions,
  discountStyleOptions,
  gstinTypeOptions,
  transformPayload,
} from "../EntityConstants";
import classes from "./index.module.css";
import { debounceBankName, debounceCategoryName } from "../entityUtils";

const AddEntity = ({
  entityRowEditData,
  onSaveEntity = () => {},
  onEscape = () => {},
}) => {
  const [formState, setFormState] = useState({
    type: customerTypeOptions[0].label,
  });
  const [errorStates, setErrorStates] = useState({});

  const [categoryList, setCategoryList] = useState([]);
  const [bankOptions, setBankOptions] = useState([]);

  const withPhoneCap = ({ value }) => value.length <= 10;
  const withLandlineCap = ({ value }) => value.length <= 15;
  const withPincodeCap = ({ value }) => value.length <= 6;

  const saveEntityRef = useRef();

  const entityNameRef = useRef("");
  const addressRef = useRef("");
  const areaRef = useRef("");
  const stateRef = useRef("");
  const categoryRef = useRef("");
  const contactNumberRef = useRef("");
  const landLineRef = useRef("");
  const emailRef = useRef("");
  const bankRef = useRef("");
  const foodLicenseRef = useRef("");
  const drugLicenseRef = useRef("");
  const panNumberRef = useRef("");
  const gstinRef = useRef("");
  const gstinTypeRef = useRef("");
  const creditDaysRef = useRef("");
  const creditLimitRef = useRef("");
  const remarkRef = useRef("");
  const discountStyleRef = useRef("");
  const entityTypeRef = useRef("");
  const pincodeRef = useRef("");
  const cityRef = useRef("");

  const { setCurrentComponent } = useContext(GlobalContext);

  const validateForm = useCallback((fieldName, data) => {
    if (fieldName === "categoryName" || !fieldName) {
      if (!data?.categoryName) {
        categoryRef.current?.focus();
        return displayToast({
          type: "error",
          title: "Please Select Category",
        });
      }
    }

    if (fieldName === "entityName" || !fieldName) {
      if (!data?.entityName) {
        entityNameRef.current?.focus();
        return displayToast({
          type: "error",
          title: "Please enter Entity Name",
        });
      }
    }
    if (
      fieldName === "contactNumber" ||
      fieldName === "landlineNumber" ||
      !fieldName
    ) {
      if (!data?.contactNumber && !data?.landlineNumber) {
        contactNumberRef.current?.focus();
        return displayToast({
          type: "error",
          title: "Please enter Contact Number or Landline Number",
        });
      }
      if (data?.contactNumber && !isValidPhoneNumber(data.contactNumber)) {
        return displayToast({
          type: "error",
          title: "Please enter a valid 10 digit mobile number.",
        });
      }
    }
    if (fieldName === "state" || !fieldName) {
      if (!data?.state && data.type === "B2B") {
        stateRef.current?.focus();
        return displayToast({
          type: "error",
          title: "Please Select State",
        });
      }
    }

    if (fieldName === "pincode" || !fieldName) {
      if (!data?.pincode && data.type === "B2B") {
        pincodeRef.current?.focus();
        return displayToast({
          type: "error",
          title: "Please enter Pincode",
        });
      }
    }

    if (fieldName === "email" || !fieldName) {
      const regex =
        /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
      if (data.email && !regex.test(data.email)) {
        displayToast({
          type: "error",
          title: "Please enter a valid email.",
        });
        setErrorStates((prev) => {
          return { ...prev, email: true };
        });
        if (!fieldName) emailRef.current.focus({ preventScroll: true });
        return false;
      }
    }

    if (fieldName === "foodLicense" || !fieldName) {
      if (!data?.foodLicense && data?.type === "B2B") {
        foodLicenseRef.current?.focus();
        return displayToast({
          type: "error",
          title: "Please enter Food License",
        });
      }
    }
    if (fieldName === "panNumber" || !fieldName) {
      if (!data?.panNumber && data?.type === "B2B") {
        panNumberRef.current?.focus();
        return displayToast({
          type: "error",
          title: "Please enter Pan Number",
        });
      }
      if (data?.panNumber && data?.panNumber.length !== 10) {
        return displayToast({
          type: "error",
          title: "Pan Number should be exactly 10 characters.",
        });
      }
    }

    if (fieldName === "gstin" || !fieldName) {
      if (!data?.gstin && data?.type === "B2B") {
        gstinRef.current?.focus();
        return displayToast({
          type: "error",
          title: "Please enter GSTIN",
        });
      }
      if (data?.gstin && !validateGstin(data?.gstin)) {
        return displayToast({
          type: "error",
          title: "Please enter Valid GSTIN",
        });
      }
    }

    if (fieldName === "gstinType" || !fieldName) {
      if (!data?.gstinType && data?.type === "B2B") {
        gstinTypeRef.current?.focus();
        return displayToast({
          type: "error",
          title: "Please enter GSTIN Type",
        });
      }
    }
    if (fieldName === "creditDays" || !fieldName) {
      if (!data?.creditDays && data?.creditLimit) {
        creditDaysRef.current?.focus();
        return displayToast({
          type: "error",
          title: "Please enter Credit Days",
        });
      }
    }

    if (fieldName === "creditLimit" || !fieldName) {
      if (!data?.creditLimit && data?.creditDays) {
        creditLimitRef.current?.focus();
        return displayToast({
          type: "error",
          title: "Please enter Credit Limit",
        });
      }
    }

    return true;
  }, []);

  const saveEntityHandler = useCallback(
    async (e) => {
      try {
        if (!validateForm(null, formState)) return;
        const payload = transformPayload({ ...formState });
        const response = await saveEntity(payload);
        if (response.status === "success") {
          onSaveEntity(response);
          return displayToast({
            type: "success",
            title: "Entity added successfully.",
          });
        }
      } catch (error) {
        console.log(error);
      }
    },
    [formState, onSaveEntity, validateForm]
  );
  useEffect(() => {
    debounceCategoryName(getCustomerCategories, "", setCategoryList);
    debounceBankName(getBankList, "", setBankOptions);
    if (!entityRowEditData) return;
    setFormState(() => {
      return {
        ...entityRowEditData,
        bankName: entityRowEditData?.entityBankData?.bankName,
        categoryName: entityRowEditData?.entityCategoryData?.name,
      };
    });
  }, [entityRowEditData]);

  const keyupListener = useCallback(
    (event) => {
      const key = event.key?.toLowerCase();
      if (key === "escape") {
        return onEscape();
      }
      if (event.ctrlKey && key === "s") {
        return saveEntityRef.current.click();
      }
    },
    [onEscape]
  );
  const handleFormChange = useCallback(
    (fieldName, fieldValue, isSelect = false, options, sendOption = false) => {
      setFormState((prev) => {
        const updatedFormState = formStateHandler(
          prev,
          fieldName,
          fieldValue,
          isSelect,
          options,
          sendOption
        );
        return updatedFormState;
      });
    },
    []
  );
  useEffect(() => {
    document.addEventListener("keyup", keyupListener, false);
    return () => {
      document.removeEventListener("keyup", keyupListener, false);
    };
  }, [keyupListener]);

  const validateGstin = useCallback((gstin) => {
    const gstinRegex = /^\d{2}[A-Z]{5}\d{4}[A-Z]{1}[A-Z\d]{1}Z[A-Z\d]{1}$/;
    if (!gstinRegex.test(gstin)) {
      displayToast({
        type: "error",
        title: "Please enter a valid GSTIN.",
      });
      return;
    }
    return true;
  }, []);
  return (
    <div>
      <div className={classes["entity-detail-label"]}>Entity Details</div>

      <Grid container spacing={2} sx={{ p: "10px 30px" }}>
        <Grid item xs={4}>
          <SelectInput
            id="add_entity_category"
            label="Category"
            ref={categoryRef}
            placeHolder="Category"
            openOnFocus={false}
            name="categoryName"
            onFocus={() => {
              setCurrentComponent("add_entity_category");
            }}
            disableClearable={false}
            onChange={(event, option) => {
              handleFormChange("categoryName", null, true, option?.name, true);
              handleFormChange("categoryId", null, true, option?.id, true);
            }}
            noOptionsText="Search Category Here..."
            isOptionEqualToValue={(option, value) => {
              return option?.id === value?.id;
            }}
            getOptionLabel={(option) => option?.name || option}
            options={categoryList}
            value={formState?.categoryName || ""}
            required={true}
            markers={JSON.stringify({
              up: "add_entity_category",
              left: "add_entity_category",
              right: "add_entity_entitytype",
              down: "add_entity_contactnumber",
            })}
            autoFocus
          />
        </Grid>
        <Grid item xs={4}>
          <SelectInput
            label="Entity Type"
            id="add_entity_entitytype"
            ref={entityTypeRef}
            placeHolder="Entity Type"
            openOnFocus={true}
            name="type"
            onFocus={() => {
              setCurrentComponent("add_entity_entitytype");
            }}
            onChange={(event, option) => {
              handleFormChange("type", null, true, option.label, true);
              if (option?.label !== "B2B") {
                handleFormChange("computeDpPercentage", false);
              }
            }}
            isOptionEqualToValue={(option, value) => {
              return option.id === value.id;
            }}
            getOptionLabel={(option) => option.label || option}
            options={customerTypeOptions}
            value={formState?.type || ""}
            required={false}
            markers={JSON.stringify({
              up: "add_entity_entitytype",
              left: "add_entity_category",
              right: "add_entity_entityname",
              down: "add_entity_address",
            })}
          />
        </Grid>
        <Grid item xs={4}>
          <TextInput
            id="add_entity_entityname"
            ref={entityNameRef}
            name="entityName"
            label="Entity Name"
            required={true}
            onFocus={() => setCurrentComponent("add_entity_entityname")}
            onChange={(e) => {
              handleFormChange("entityName", e.target.value);
              if (errorStates?.entityName)
                setErrorStates((prev) => {
                  return { ...prev, entityName: false };
                });
            }}
            value={formState?.entityName || ""}
            onBlur={() => {
              if (formState.entityName) validateForm("entityName", formState);
            }}
            inputProps={{
              "data-markers": JSON.stringify({
                up: "add_entity_entityname",
                left: "add_entity_entitytype",
                right: "add_entity_contactnumber",
                down: "add_entity_state",
              }),
            }}
            error={errorStates?.entityName}
            // autoFocus={medicine ? true : false}
          />
        </Grid>
      </Grid>

      <Grid container spacing={2} sx={{ p: "10px 30px" }}>
        <Grid item xs={4}>
          <NumberInput
            id="add_entity_contactnumber"
            ref={contactNumberRef}
            name="contactNumber"
            label="Contact Number"
            isAllowed={withPhoneCap}
            allowNegative={false}
            decimalScale={0}
            required={!formState?.landlineNumber}
            onFocus={() => setCurrentComponent("add_entity_contactnumber")}
            onValueChange={(values, e) => {
              handleFormChange("contactNumber", values?.floatValue || null);
              if (errorStates?.contactNumber)
                setErrorStates((prev) => {
                  return { ...prev, contactNumber: false };
                });
            }}
            value={formState?.contactNumber || ""}
            onBlur={() => {
              if (formState.contactNumber)
                validateForm("contactNumber", formState);
            }}
            inputProps={{
              "data-markers": JSON.stringify({
                up: "add_entity_category",
                left: "add_entity_entityname",
                right: "add_entity_landlinenumber",
                down: "add_entity_city",
              }),
            }}
            error={errorStates?.contactNumber}
          />
        </Grid>
        <Grid item xs={4}>
          <TextInput
            id="add_entity_landlinenumber"
            ref={landLineRef}
            name="landLineNumber"
            label="Phone/Landline Number"
            onFocus={() => setCurrentComponent("add_entity_landlinenumber")}
            onChange={(e) => {
              if (e.target.value?.length > 20) return;
              const landlineRegex = /^[0-9]*$/;
              if (!landlineRegex.test(e.target.value)) {
                return;
              }
              handleFormChange("landlineNumber", e.target.value || null);
              if (errorStates?.landlineNumber)
                setErrorStates((prev) => {
                  return { ...prev, landlineNumber: false };
                });
            }}
            required={!formState?.contactNumber}
            value={formState?.landlineNumber || ""}
            onBlur={() => {
              if (formState.landlineNumber)
                validateForm("landlineNumber", formState);
            }}
            inputProps={{
              "data-markers": JSON.stringify({
                up: "add_entity_entitytype",
                left: "add_entity_contactnumber",
                right: "add_entity_address",
                down: "add_entity_city",
              }),
            }}
            error={errorStates?.landlineNumber}
          />
        </Grid>
        <Grid item xs={4}>
          <TextInput
            id="add_entity_address"
            ref={addressRef}
            name="address"
            label="Address"
            onFocus={() => setCurrentComponent("add_entity_address")}
            onChange={(e) => {
              handleFormChange("address", e.target.value);
              if (errorStates?.address)
                setErrorStates((prev) => {
                  return { ...prev, address: false };
                });
            }}
            value={formState?.address || ""}
            onBlur={() => {
              if (formState.address) validateForm("entityAddress", formState);
            }}
            inputProps={{
              "data-markers": JSON.stringify({
                up: "add_entity_entitytype",
                left: "add_entity_landlinenumber",
                right: "add_entity_state",
                down: "add_entity_area",
              }),
            }}
            error={errorStates?.address}
            required={false}
            // autoFocus={medicine ? true : false}
          />
        </Grid>
      </Grid>
      <Grid container spacing={2} sx={{ p: "10px 30px" }}>
        <Grid item xs={4}>
          <SelectInput
            disableClearable={false}
            label="State"
            id="add_entity_state"
            ref={stateRef}
            placeHolder="State"
            openOnFocus={true}
            name="state"
            onFocus={() => {
              setCurrentComponent("add_entity_state");
            }}
            onChange={(event, option) => {
              handleFormChange("state", null, true, option?.label, true);
            }}
            isOptionEqualToValue={(option, value) => {
              return option.id === value.id;
            }}
            getOptionLabel={(option) => option.label || option}
            options={stateCodes}
            value={formState?.state || ""}
            required={formState.type === "B2B"}
            markers={JSON.stringify({
              up: "add_entity_entityname",
              left: "add_entity_address",
              right: "add_entity_city",
              down: "add_entity_pincode",
            })}
          />
        </Grid>
        <Grid item xs={4}>
          <TextInput
            id="add_entity_city"
            ref={cityRef}
            name="city"
            label="City"
            onFocus={() => setCurrentComponent("add_entity_city")}
            onChange={(e) => {
              handleFormChange("city", e.target.value);
              if (errorStates?.city)
                setErrorStates((prev) => {
                  return { ...prev, city: false };
                });
            }}
            value={formState?.city || ""}
            onBlur={() => {
              if (formState.city) validateForm("city", formState);
            }}
            inputProps={{
              "data-markers": JSON.stringify({
                up: "add_entity_contactnumber",
                left: "add_entity_state",
                right: "add_entity_area",
                down: "add_entity_email",
              }),
            }}
            error={errorStates?.city}
            required={false}
          />
        </Grid>
        <Grid item xs={4}>
          <TextInput
            id="add_entity_area"
            ref={areaRef}
            name="area"
            label="Area"
            onFocus={() => setCurrentComponent("add_entity_area")}
            onChange={(e) => {
              handleFormChange("area", e.target.value);
              if (errorStates?.area)
                setErrorStates((prev) => {
                  return { ...prev, area: false };
                });
            }}
            value={formState?.area || ""}
            onBlur={() => {
              if (formState.area) validateForm("area", formState);
            }}
            inputProps={{
              "data-markers": JSON.stringify({
                up: "add_entity_address",
                left: "add_entity_city",
                right: "add_entity_pincode",
                down: "add_entity_pannumber",
              }),
            }}
            error={errorStates?.entityArea}
            required={false}
          />
        </Grid>
      </Grid>
      <Grid container spacing={2} sx={{ p: "10px 30px" }}>
        <Grid item xs={4}>
          <NumberInput
            id="add_entity_pincode"
            ref={pincodeRef}
            name="pincode"
            label="Pincode"
            isAllowed={withPincodeCap}
            allowNegative={false}
            required={formState.type === "B2B"}
            onFocus={() => setCurrentComponent("add_entity_pincode")}
            onValueChange={(values, e) => {
              handleFormChange("pincode", values.formattedValue);
              if (errorStates?.pincode)
                setErrorStates((prev) => {
                  return { ...prev, pincode: false };
                });
            }}
            value={formState?.pincode || ""}
            onBlur={() => {
              if (formState.pincode) validateForm("pincode", formState);
            }}
            inputProps={{
              "data-markers": JSON.stringify({
                up: "add_entity_state",
                left: "add_entity_area",
                right: "add_entity_email",
                down: "add_entity_gstintype",
              }),
            }}
            error={errorStates?.entityPincode}
          />
        </Grid>
        <Grid item xs={4}>
          <TextInput
            id="add_entity_email"
            ref={emailRef}
            type="email"
            name="email"
            label="Email"
            required={false}
            onFocus={() => setCurrentComponent("add_entity_email")}
            onChange={(e) => {
              handleFormChange("email", e.target.value.replace(/\s/g, ""));
              if (errorStates?.email)
                setErrorStates((prev) => {
                  return { ...prev, email: false };
                });
            }}
            value={formState?.email || ""}
            onBlur={() => {
              if (formState.email) validateForm("email", formState);
            }}
            inputProps={{
              "data-markers": JSON.stringify({
                up: "add_entity_city",
                left: "add_entity_pincode",
                right: "add_entity_pannumber",
                down: "add_entity_gstin",
              }),
            }}
            error={errorStates?.entityEmail}
          />
        </Grid>
        <Grid item xs={4}>
          <TextInput
            id="add_entity_pannumber"
            ref={panNumberRef}
            type="text"
            name="panNumber"
            label="Pan Number"
            required={formState.type === "B2B"}
            onFocus={() => setCurrentComponent("add_entity_pannumber")}
            onKeyDown={(e) => {
              if (e.key === " ") {
                e.preventDefault();
              }
              if (!/^[A-Za-z0-9]*$/.test(e.key)) {
                e.preventDefault();
              }
            }}
            onChange={(e) => {
              if (e.target.value?.length > 10) return;
              handleFormChange("panNumber", e.target.value.toUpperCase());
              if (errorStates?.panNumber)
                setErrorStates((prev) => {
                  return { ...prev, panNumber: false };
                });
            }}
            value={formState?.panNumber || ""}
            onBlur={() => {
              if (formState.panNumber) validateForm("panNumber", formState);
            }}
            inputProps={{
              "data-markers": JSON.stringify({
                up: "add_entity_area",
                left: "add_entity_email",
                right: "add_entity_gstintype",
                down: "add_entity_foodLicense",
              }),
            }}
            error={errorStates?.entitypanNumber}
          />
        </Grid>
      </Grid>

      <Grid container spacing={2} sx={{ p: "10px 30px" }}>
        <Grid item xs={4}>
          <SelectInput
            label="GSTIN Type"
            id="add_entity_gstintype"
            ref={gstinTypeRef}
            placeHolder="GSTIN Type"
            openOnFocus={true}
            name="gstinType"
            required={formState.type === "B2B"}
            onFocus={() => {
              setCurrentComponent("add_entity_gstintype");
            }}
            onChange={(event, option) => {
              handleFormChange("gstinType", null, true, option.label, true);
            }}
            isOptionEqualToValue={(option, value) => {
              return option.id === value.id;
            }}
            getOptionLabel={(option) => option.label || option}
            options={gstinTypeOptions}
            value={formState?.gstinType || ""}
            markers={JSON.stringify({
              up: "add_entity_pincode",
              left: "add_entity_pannumber",
              right: "add_entity_gstin",
              down: "add_entity_drugLicense",
            })}
          />
        </Grid>
        <Grid item xs={4}>
          <TextInput
            id="add_entity_gstin"
            ref={gstinRef}
            type="text"
            name="gstin"
            label="GSTIN"
            required={formState.type === "B2B"}
            onFocus={() => setCurrentComponent("add_customer_gstin")}
            onChange={(e) => {
              handleFormChange("gstin", e.target.value.toUpperCase());
              if (errorStates?.gstin)
                setErrorStates((prev) => {
                  return { ...prev, gstin: false };
                });
            }}
            value={formState?.gstin || ""}
            onBlur={() => {
              if (formState.gstin) validateForm("gstin", formState);
            }}
            inputProps={{
              "data-markers": JSON.stringify({
                up: "add_entity_email",
                left: "add_entity_gstintype",
                right: "add_entity_foodLicense",
                down: "add_entity_discountstyle",
              }),
            }}
            error={errorStates?.gstin}
          />
        </Grid>
        <Grid item xs={4}>
          <TextInput
            id="add_entity_foodLicense"
            ref={foodLicenseRef}
            type="text"
            name="foodLicense"
            label="Food License"
            required={formState.type === "B2B"}
            onFocus={() => setCurrentComponent("add_entity_foodLicense")}
            onChange={(e) => {
              const value = e.target.value;
              if (/^\d{0,14}$/.test(value)) {
                handleFormChange("foodLicense", value);
              }
              //handleFormChange("foodLicense", e.target.value);
              if (errorStates?.foodLicense)
                setErrorStates((prev) => {
                  return { ...prev, foodLicense: false };
                });
            }}
            value={formState?.foodLicense || ""}
            onBlur={() => {
              if (formState.foodLicense) validateForm("foodLicense", formState);
            }}
            inputProps={{
              "data-markers": JSON.stringify({
                up: "add_entity_pannumber",
                left: "add_entity_gstin",
                right: "add_entity_drugLicense",
                down: "add_entity_creditdays",
              }),
            }}
            error={errorStates?.foodLicense}
          />
        </Grid>
      </Grid>

      <Grid container spacing={2} sx={{ p: "10px 30px" }}>
        <Grid item xs={4}>
          <TextInput
            id="add_entity_drugLicense"
            ref={drugLicenseRef}
            type="text"
            name="drugLicense"
            label="Drug License"
            required={formState.type === "B2B"}
            onFocus={() => setCurrentComponent("add_entity_drugLicense")}
            onChange={(e) => {
              handleFormChange("drugLicense", e.target.value);
              if (errorStates?.drugLicense)
                setErrorStates((prev) => {
                  return { ...prev, drugLicense: false };
                });
            }}
            value={formState?.drugLicense || ""}
            onBlur={() => {
              if (formState.drugLicense) validateForm("drugLicense", formState);
            }}
            inputProps={{
              "data-markers": JSON.stringify({
                up: "add_entity_gstintype",
                left: "add_entity_foodLicense",
                right: "add_entity_discountstyle",
                down: "add_entity_creditlimit",
              }),
            }}
            error={errorStates?.drugLicense}
          />
        </Grid>
        <Grid item xs={4}>
          <SelectInput
            label="Discount Style"
            id="add_entity_discountstyle"
            ref={discountStyleRef}
            placeHolder="Discount Style"
            openOnFocus={true}
            name="discountStyle"
            onFocus={() => {
              setCurrentComponent("add_entity_discountstyle");
            }}
            onChange={(event, option) => {
              handleFormChange("discountStyle", null, true, option.label, true);
            }}
            isOptionEqualToValue={(option, value) => {
              return option.id === value.id;
            }}
            getOptionLabel={(option) => option.label || option}
            options={discountStyleOptions}
            value={formState?.discountStyle || ""}
            required={false}
            markers={JSON.stringify({
              up: "add_entity_gstin",
              left: "add_entity_drugLicense",
              right: "add_entity_creditdays",
              down: "add_entity_remarks",
            })}
          />
        </Grid>
        <Grid item xs={4}>
          <NumberInput
            id="add_entity_creditdays"
            ref={creditDaysRef}
            name="creditDays"
            label="Credit Days"
            decimalScale={0}
            allowNegative={false}
            required={formState.creditLimit || formState.creditDays}
            onFocus={() => setCurrentComponent("add_entity_creditdays")}
            onValueChange={(values, e) => {
              handleFormChange("creditDays", values?.floatValue);
              if (errorStates?.creditDays)
                setErrorStates((prev) => {
                  return { ...prev, creditDays: false };
                });
            }}
            value={formState?.creditDays || ""}
            onBlur={() => {
              if (formState.creditDays) validateForm("creditDays", formState);
            }}
            inputProps={{
              "data-markers": JSON.stringify({
                up: "add_entity_foodLicense",
                left: "add_entity_discountstyle",
                right: "add_entity_creditlimit",
                down: "add_entity_bank_name",
              }),
            }}
          />
        </Grid>
      </Grid>

      <Grid container spacing={2} sx={{ p: "10px 30px" }}>
        <Grid item xs={4}>
          <NumberInput
            id="add_entity_creditlimit"
            ref={creditLimitRef}
            name="creditLimit"
            label="Credit Limit"
            allowNegative={false}
            decimalScale={0}
            required={formState.creditDays || formState.creditLimit}
            onFocus={() => setCurrentComponent("add_entity_creditlimit")}
            onValueChange={(values, e) => {
              handleFormChange("creditLimit", values.floatValue);
              if (errorStates?.creditLimit)
                setErrorStates((prev) => {
                  return { ...prev, creditLimit: false };
                });
            }}
            value={formState?.creditLimit || ""}
            onBlur={() => {
              if (formState.creditLimit) validateForm("creditLimit", formState);
            }}
            inputProps={{
              "data-markers": JSON.stringify({
                up: "add_entity_drugLicense",
                left: "add_entity_creditdays",
                right: "add_entity_remarks",
                down: "add_entity_creditlimit",
              }),
            }}
          />
        </Grid>
        <Grid item xs={4}>
          <TextInput
            id="add_entity_remarks"
            ref={remarkRef}
            type="text"
            name="remarks"
            label="Remark"
            required={false}
            onFocus={() => setCurrentComponent("add_entity_remarks")}
            onChange={(e) => {
              handleFormChange("remarks", e.target.value);
              if (errorStates?.remarks)
                setErrorStates((prev) => {
                  return { ...prev, remarks: false };
                });
            }}
            value={formState?.remarks || ""}
            onBlur={() => {
              if (formState.remarks) validateForm("remarks", formState);
            }}
            maxInputLength={2000}
            inputProps={{
              "data-markers": JSON.stringify({
                up: "add_entity_discountstyle",
                left: "add_entity_creditlimit",
                right: "add_entity_bank_name",
                down: "add_entity_remarks",
              }),
            }}
            error={errorStates?.remarks}
          />
        </Grid>
        <Grid item xs={4}>
          <SelectInput
            label="Bank"
            id="add_entity_bank_name"
            ref={bankRef}
            openOnFocus={true}
            name="bankName"
            onFocus={() => {
              setCurrentComponent("add_entity_bank_name");
            }}
            onChange={(event, option) => {
              handleFormChange("bankName", null, true, option?.bankName, true);
              handleFormChange("bankId", null, true, option?.bankId, true);
            }}
            disableClearable={false}
            isOptionEqualToValue={(option, value) => {
              return option?.bankId === value?.bankId;
            }}
            options={bankOptions}
            value={formState?.bankName || ""}
            getOptionLabel={(option) => option?.bankName || option}
            // getOptionLabel={(option) => {
            //   console.log(option);
            //   return `${option?.bankName} - ${option?.accountNo}`;
            // }}
            required={false}
            renderOption={(props, option) => {
              return (
                <li {...props} key={option.id}>
                  {option.bankName} {option.accountNo}
                </li>
              );
            }}
            markers={JSON.stringify({
              up: "add_entity_creditdays",
              left: "add_entity_remarks",
              right:
                formState?.type === "B2B"
                  ? "add_entity_compute_dp_percentage"
                  : "add_entity_bank_name",
              down: "add_entity_bank_name",
            })}
          />
        </Grid>
        {formState?.type === "B2B" && (
          <Grid item xs={4}>
            <Checkbox
              id="add_entity_compute_dp_percentage"
              label="Compute DP Percentage"
              name="computeDpPercentage"
              onChange={(e) => {
                handleFormChange("computeDpPercentage", e.target.checked);
              }}
              checked={formState?.computeDpPercentage || false}
              onFocus={() => {
                setCurrentComponent("add_entity_compute_dp_percentage");
              }}
              inputProps={{
                "data-markers": JSON.stringify({
                  up: "add_entity_creditdays",
                  left: "add_entity_bank_name",
                  right: "add_entity_compute_dp_percentage",
                  down: "add_entity_compute_dp_percentage",
                }),
              }}
            />
          </Grid>
        )}
      </Grid>

      <div className={classes["add-entity-save-button-wrapper"]}>
        <Button
          ref={saveEntityRef}
          endIcon={<Shortcut keys={["Ctrl", "S"]} />}
          label="Save Entity"
          color="violet"
          onClick={saveEntityHandler}
        />
      </div>
    </div>
  );
};

export default AddEntity;
